/**
 * Names-only structure editor for the create wizard's step 2 (INT-0039):
 * competition name, contests (tournament flow) and divisions, each editable
 * with add/remove. Prefilled from the bootstrap defaults so what the
 * organizer previews is exactly what the server would have named. Format and
 * capacity stay hidden (bootstrap defaults apply; see StructureEditor for the
 * full form used by Get Started and the carried-teaser confirm step).
 * Presentational: state lives with the caller via value/onChange.
 *
 * Rendered as a TREE (owner 2026-10-16): no "Contests" / "Divisions" model
 * labels, indentation + connector lines carry the hierarchy, and every
 * contest owns its OWN editable division list (a new contest starts as a copy
 * of the first one's). Entries carry an optional `id` so the same editor can
 * edit an already-built competition (the "go back" edit mode reconciles by
 * id). The aria-labels keep the accessible names the tests and tours pin
 * ("Contest 1 name", "Division 2 name", "Add division", ...).
 */
import React from 'react';
import { Plus, Trash2, Trophy, CalendarRange } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export interface StructureDivisionDraft {
  id?: string;
  name: string;
}

export interface StructureContestDraft {
  id?: string;
  name: string;
  divisions: StructureDivisionDraft[];
}

export interface StructureNamesDraft {
  name: string;
  /** Tournament flow: each contest carries its own divisions. Empty for a league. */
  contests: StructureContestDraft[];
  /** League flow divisions (ignored for a tournament, whose divisions live per contest). */
  divisions: StructureDivisionDraft[];
}

export type StructureContestType = 'tournament' | 'season';

const MAX_NAME = 80;

export const divisionNames = (list: StructureDivisionDraft[]): string[] => list.map((d) => d.name);

const divisionLists = (draft: StructureNamesDraft, contestType: StructureContestType): StructureDivisionDraft[][] =>
  contestType === 'tournament' ? draft.contests.map((c) => c.divisions) : [draft.divisions];

export function validateStructureNames(draft: StructureNamesDraft, contestType: StructureContestType): string | null {
  if (!draft.name.trim()) return 'Give your competition a name.';
  if (contestType === 'tournament') {
    if (!draft.contests.length) return 'Add at least one contest.';
    if (draft.contests.some((c) => !c.name.trim())) return 'Give every contest a name.';
  }
  const lists = divisionLists(draft, contestType);
  if (lists.some((l) => !l.length)) return 'Add at least one division.';
  if (lists.some((l) => l.some((d) => !d.name.trim()))) return 'Give every division a name.';
  return null;
}

/** Trim, cap, and collapse duplicates (case-insensitive) within each list; ids ride along. */
export function cleanStructureNames(draft: StructureNamesDraft): StructureNamesDraft {
  const dedupe = (list: StructureDivisionDraft[]) => {
    const seen = new Set<string>();
    const out: StructureDivisionDraft[] = [];
    list.forEach((raw) => {
      const name = raw.name.trim().slice(0, MAX_NAME);
      if (!name || seen.has(name.toLowerCase())) return;
      seen.add(name.toLowerCase());
      out.push(raw.id ? { id: raw.id, name } : { name });
    });
    return out;
  };
  const seenContests = new Set<string>();
  const contests: StructureContestDraft[] = [];
  draft.contests.forEach((c) => {
    const name = c.name.trim().slice(0, MAX_NAME);
    if (!name || seenContests.has(name.toLowerCase())) return;
    seenContests.add(name.toLowerCase());
    contests.push(c.id ? { id: c.id, name, divisions: dedupe(c.divisions) } : { name, divisions: dedupe(c.divisions) });
  });
  return { name: draft.name.trim().slice(0, MAX_NAME), contests, divisions: dedupe(draft.divisions) };
}

interface StructureNamesEditorProps {
  value: StructureNamesDraft;
  onChange: (next: StructureNamesDraft) => void;
  contestType: StructureContestType;
  /** Collapsed = one summary line + "Edit names" (owner 2026-10-12: show what
   * we build without asking for work; expanding is the organizer's choice). */
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** Edit mode (the wizard's "go back"): always expanded, no Done button. */
  collapsible?: boolean;
}

/** "Winter 2027 Tournament. MENS, WOMENS. 3 divisions each." (the total when the counts differ) */
export function summarizeStructure(draft: StructureNamesDraft, contestType: StructureContestType): string {
  const parts = [draft.name.trim() || (contestType === 'tournament' ? 'Tournament' : 'League')];
  let n: number;
  let suffix = '';
  if (contestType === 'tournament') {
    const contests = draft.contests.filter((c) => c.name.trim());
    if (contests.length) parts.push(contests.map((c) => c.name.trim()).join(', '));
    const counts = contests.map((c) => c.divisions.filter((d) => d.name.trim()).length);
    const allSame = counts.length > 0 && counts.every((x) => x === counts[0]);
    n = allSame ? counts[0] : counts.reduce((a, b) => a + b, 0);
    suffix = allSame && counts.length > 1 ? ' each' : '';
  } else {
    n = draft.divisions.filter((d) => d.name.trim()).length;
  }
  parts.push(`${n} division${n === 1 ? '' : 's'}${suffix}`);
  return parts.join('. ') + '.';
}

const TICK = 'relative pl-4 before:absolute before:left-0 before:top-1/2 before:h-px before:w-3 before:bg-border sm:pl-5 sm:before:w-4';

export default function StructureNamesEditor({ value, onChange, contestType, expanded, onExpandedChange, collapsible = true }: StructureNamesEditorProps) {
  const patch = (p: Partial<StructureNamesDraft>) => onChange({ ...value, ...p });
  const isTournament = contestType === 'tournament';

  if (collapsible && !expanded) {
    return (
      <div className="flex flex-col gap-3 rounded-2xl bg-card p-5 shadow-lg ring-1 ring-border sm:flex-row sm:items-center sm:justify-between" data-tour="create-structure-panel">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">Your first competition</h2>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="structure-summary">{summarizeStructure(value, contestType)}</p>
          <p className="mt-1 text-xs text-muted-foreground/70">You can change all of this later from the Competitions tab.</p>
        </div>
        <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => onExpandedChange(true)} data-tour="create-structure-edit">
          Edit names
        </Button>
      </div>
    );
  }

  // Division list mutations. `contestIndex` null = the league flow's list.
  const getDivisions = (contestIndex: number | null) => (contestIndex === null ? value.divisions : value.contests[contestIndex].divisions);
  const setDivisions = (contestIndex: number | null, divisions: StructureDivisionDraft[]) => {
    if (contestIndex === null) return patch({ divisions });
    return patch({ contests: value.contests.map((c, i) => (i === contestIndex ? { ...c, divisions } : c)) });
  };
  const setContestName = (i: number, name: string) => patch({ contests: value.contests.map((c, idx) => (idx === i ? { ...c, name } : c)) });
  const removeContest = (i: number) => patch({ contests: value.contests.filter((_, idx) => idx !== i) });
  // A new contest starts with the FIRST contest's division NAMES (WOMENS
  // usually mirrors MENS), as fresh entries (no ids), fully editable afterwards.
  const addContest = () => patch({ contests: [...value.contests, { name: '', divisions: (value.contests[0]?.divisions || []).map((d) => ({ name: d.name })) }] });

  const removeButton = (label: string, onClick: () => void) => (
    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground" aria-label={label} onClick={onClick}>
      <Trash2 className="h-4 w-4" />
    </Button>
  );

  /** The editable division branch of one contest (or of the league). */
  const divisionsBranch = (contestIndex: number | null) => {
    const items = getDivisions(contestIndex);
    // Division labels are numbered PER contest ("Division 2 name") on the
    // first contest, and prefixed by the contest name after it so every
    // input has a unique accessible name.
    const scope = contestIndex === null || contestIndex === 0 ? '' : `${value.contests[contestIndex].name.trim() || `Contest ${contestIndex + 1}`} `;
    return (
      <ul className="ml-3 space-y-2 border-l border-border py-1 sm:ml-4">
        {items.map((item, i) => (
          <li key={`div-${contestIndex ?? 'league'}-${item.id || i}`} className={`${TICK} flex items-center gap-2`}>
            <Input
              type="text"
              value={item.name}
              maxLength={MAX_NAME}
              aria-label={`${scope}Division ${i + 1} name`}
              placeholder="Name"
              className="h-9 text-sm sm:text-base"
              onChange={(e) => setDivisions(contestIndex, items.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))}
            />
            {removeButton(`Remove ${scope.toLowerCase()}division ${item.name.trim() || i + 1}`, () => setDivisions(contestIndex, items.filter((_, idx) => idx !== i)))}
          </li>
        ))}
        <li className={TICK}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-muted-foreground hover:text-foreground"
            aria-label={`${scope}Add division`}
            onClick={() => setDivisions(contestIndex, [...items, { name: '' }])}
            data-tour={contestIndex ? `create-structure-add-division-${contestIndex + 1}` : 'create-structure-add-division'}
          >
            <Plus className="mr-1 h-4 w-4" /> Add another
          </Button>
        </li>
      </ul>
    );
  };

  return (
    <div className="space-y-4 rounded-2xl bg-card p-5 shadow-lg ring-1 ring-border" data-tour="create-structure-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">Your first competition</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Rename anything now. You can change all of this later from the Competitions tab.
          </p>
        </div>
        {collapsible && (
          <Button type="button" variant="ghost" size="sm" className="shrink-0" onClick={() => onExpandedChange(false)} data-tour="create-structure-done">
            Done
          </Button>
        )}
      </div>

      {/* Root node */}
      <div className="flex items-center gap-2">
        {isTournament ? <Trophy className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" /> : <CalendarRange className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
        <Input
          id="structure-name"
          type="text"
          value={value.name}
          maxLength={MAX_NAME}
          aria-label={isTournament ? 'Tournament name' : 'League name'}
          placeholder={isTournament ? 'Tournament name' : 'League name'}
          className="h-10 font-medium"
          data-tour="create-structure-name"
          onChange={(e) => patch({ name: e.target.value })}
        />
      </div>

      {isTournament ? (
        <ul className="ml-2 space-y-2 border-l border-border py-1">
          {value.contests.map((c, i) => (
            <li key={`contest-${c.id || i}`} className="relative pl-4 before:absolute before:left-0 before:top-5 before:h-px before:w-3 before:bg-border sm:pl-5 sm:before:w-4">
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={c.name}
                  maxLength={MAX_NAME}
                  aria-label={`Contest ${i + 1} name`}
                  placeholder="Name"
                  className="h-9 text-sm sm:text-base"
                  onChange={(e) => setContestName(i, e.target.value)}
                />
                {removeButton(`Remove contest ${c.name.trim() || i + 1}`, () => removeContest(i))}
              </div>
              {divisionsBranch(i)}
            </li>
          ))}
          <li className={TICK}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-muted-foreground hover:text-foreground"
              aria-label="Add contest"
              onClick={addContest}
              data-tour="create-structure-add-contest"
            >
              <Plus className="mr-1 h-4 w-4" /> Add another
            </Button>
          </li>
        </ul>
      ) : (
        <div className="ml-2">{divisionsBranch(null)}</div>
      )}
    </div>
  );
}
